import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2,
  Users,
  BarChart3,
  Search,
  ShieldCheck,
  Ban,
  CheckCircle2,
  KeyRound,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Pencil,
  Check,
  X,
  Trash2,
  Megaphone,
  Link2,
  QrCode,
  Plus,
  Download,
  Copy,
  ArrowUpDown,
} from 'lucide-react';
import { StatCard } from '@/components/ui/StatCard';
import { Badge } from '@/components/ui/Badge';
import {
  useSACompanies,
  useSAUsers,
  useSAAnalytics,
  useSAEventAnalytics,
  useSACompanyDetail,
  useSetEventQuota,
  useBulkSetEventQuota,
  useToggleUserDisabled,
  useTriggerPasswordReset,
  useSASalesReps,
  useCreateSalesRep,
  useUpdateSalesRep,
  useSADeletePreview,
  useDeleteCompany,
  type SACompany,
  type SAUser,
  type SAEvent,
  type SASalesRep,
  type SortState,
} from '@/hooks/useSuperAdmin';
import { exportToCSV } from '@/utils/exportCsv';

type Tab = 'organizations' | 'users' | 'analytics' | 'sales-team';

export default function SuperAdminPage() {
  const [tab, setTab] = useState<Tab>('organizations');

  const TABS: { key: Tab; label: string; icon: typeof Building2 }[] = [
    { key: 'organizations', label: 'Organizations', icon: Building2 },
    { key: 'users', label: 'Users', icon: Users },
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
    { key: 'sales-team', label: 'Sales Team', icon: Megaphone },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <ShieldCheck size={24} className="text-[#523EF7]" />
        <h1 className="text-2xl font-bold text-[#222222]">Platform Admin</h1>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 bg-white border border-[#DDDDDD] rounded-xl p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key
                ? 'bg-[#523EF7]/10 text-[#523EF7]'
                : 'text-[#3F3F3F] hover:bg-[#F2F2F2]'
            }`}
          >
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'organizations' && <OrganizationsTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'analytics' && <AnalyticsTab />}
      {tab === 'sales-team' && <SalesTeamTab />}
    </div>
  );
}

// ─── Organizations Tab ──────────────────────────────────────────────

function OrganizationsTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ field: 'createdAt', order: 'desc' });
  const { data, isLoading } = useSACompanies(page, search, sort);

  const handleSort = (field: string) => {
    setSort((prev) =>
      prev.field === field
        ? { field, order: prev.order === 'asc' ? 'desc' : 'asc' }
        : { field, order: 'asc' },
    );
    setPage(1);
  };

  const handleExport = () => {
    if (!data?.companies) return;
    exportToCSV(
      ['Organization', 'Created', 'Referred By', 'Users', 'Leads', 'Events'],
      data.companies.map((co) => [
        co.name,
        formatDate(co.createdAt),
        co.referredBy?.name || '',
        co.userCount,
        co.leadCount,
        co.eventCount,
      ]),
      'organizations-export',
    );
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search organizations..." />
        <ExportButton onClick={handleExport} disabled={!data?.companies?.length} />
      </div>

      <div className="bg-white border border-[#DDDDDD] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#DDDDDD] bg-[#F7F7F7]">
              <th className="text-left px-4 py-3 font-medium text-[#9A9A9A] text-xs uppercase tracking-wider w-8" />
              <SortableHeader label="Organization" field="name" sort={sort} onSort={handleSort} />
              <SortableHeader label="Created" field="createdAt" sort={sort} onSort={handleSort} />
              <th className="text-left px-4 py-3 font-medium text-[#9A9A9A] text-xs uppercase tracking-wider">Referred By</th>
              <SortableHeader label="Users" field="userCount" sort={sort} onSort={handleSort} align="center" />
              <SortableHeader label="Leads" field="leadCount" sort={sort} onSort={handleSort} align="center" />
              <SortableHeader label="Events" field="eventCount" sort={sort} onSort={handleSort} align="center" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#9A9A9A]">Loading...</td></tr>
            ) : data?.companies.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#9A9A9A]">No organizations found</td></tr>
            ) : (
              data?.companies.map((co) => (
                <CompanyRow
                  key={co.id}
                  company={co}
                  isExpanded={expandedId === co.id}
                  onToggle={() => setExpandedId(expandedId === co.id ? null : co.id)}
                  onDeleted={() => setExpandedId(null)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && data.totalPages > 1 && (
        <Pagination page={page} totalPages={data.totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}

function CompanyRow({ company, isExpanded, onToggle, onDeleted }: {
  company: SACompany;
  isExpanded: boolean;
  onToggle: () => void;
  onDeleted: () => void;
}) {
  const { data: detail } = useSACompanyDetail(isExpanded ? company.id : null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const bulkQuota = useBulkSetEventQuota();
  const [bulkValue, setBulkValue] = useState('');
  const [showBulkInput, setShowBulkInput] = useState(false);

  const handleBulkQuota = () => {
    const num = parseInt(bulkValue);
    if (isNaN(num) || num < 0) return;
    bulkQuota.mutate({ companyId: company.id, checksLimit: num }, {
      onSuccess: () => { setShowBulkInput(false); setBulkValue(''); },
    });
  };

  return (
    <>
      <tr
        className="border-b border-[#DDDDDD] hover:bg-[#F7F7F7] cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <td className="px-4 py-3 text-[#9A9A9A]">
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-4 py-3 font-medium text-[#222222]">{company.name}</td>
        <td className="px-4 py-3 text-[#3F3F3F]">{formatDate(company.createdAt)}</td>
        <td className="px-4 py-3 text-[#3F3F3F]">
          {company.referredBy
            ? <Badge variant="info">{company.referredBy.name}</Badge>
            : <span className="text-[#9A9A9A]">--</span>}
        </td>
        <td className="px-4 py-3 text-center text-[#3F3F3F]">{company.userCount}</td>
        <td className="px-4 py-3 text-center text-[#3F3F3F]">{company.leadCount}</td>
        <td className="px-4 py-3 text-center text-[#3F3F3F]">{company.eventCount}</td>
      </tr>
      {isExpanded && detail && (
        <tr>
          <td colSpan={7} className="bg-[#F7F7F7] px-6 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-medium text-[#9A9A9A] uppercase tracking-wider mb-2">Users</h4>
                <div className="space-y-1">
                  {detail.users.map((u) => (
                    <div key={u.id} className="flex items-center gap-2 text-sm">
                      <span className="text-[#222222] font-medium">{u.fullName}</span>
                      <span className="text-[#9A9A9A]">{u.email}</span>
                      <Badge variant={u.role === 'admin' ? 'info' : 'neutral'}>{u.role}</Badge>
                      {u.isDisabled && <Badge variant="danger">disabled</Badge>}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-medium text-[#9A9A9A] uppercase tracking-wider">Events</h4>
                  {!showBulkInput ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowBulkInput(true); }}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#523EF7] hover:bg-[#523EF7]/10 rounded-lg transition-colors"
                    >
                      <Pencil size={12} />
                      Set All Quotas
                    </button>
                  ) : (
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="number"
                        value={bulkValue}
                        onChange={(e) => setBulkValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleBulkQuota();
                          if (e.key === 'Escape') setShowBulkInput(false);
                        }}
                        placeholder="Limit"
                        className="w-20 px-2 py-1 text-xs border border-[#DDDDDD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#523EF7]/30"
                        autoFocus
                      />
                      <button onClick={handleBulkQuota} disabled={bulkQuota.isPending} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded">
                        <Check size={14} />
                      </button>
                      <button onClick={() => setShowBulkInput(false)} className="p-1 text-[#9A9A9A] hover:bg-[#F2F2F2] rounded">
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  {detail.events.map((e) => (
                    <div key={e.id} className="flex items-center gap-2 text-sm">
                      <span className="text-[#222222]">{e.name}</span>
                      <span className="text-[#9A9A9A]">{e.checksUsed}/{e.checksLimit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Delete button */}
            <div className="mt-4 pt-3 border-t border-[#DDDDDD]">
              <button
                onClick={(e) => { e.stopPropagation(); setShowDeleteDialog(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#9A9A9A] hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
              >
                <Trash2 size={14} />
                Delete organization
              </button>
            </div>
          </td>
        </tr>
      )}
      {showDeleteDialog && (
        <DeleteCompanyDialog
          companyId={company.id}
          companyName={company.name}
          onClose={() => setShowDeleteDialog(false)}
          onDeleted={() => { setShowDeleteDialog(false); onDeleted(); }}
        />
      )}
    </>
  );
}

// ─── Delete Company Dialog ──────────────────────────────────────────

function DeleteCompanyDialog({ companyId, companyName, onClose, onDeleted }: {
  companyId: string;
  companyName: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { data: preview, isLoading } = useSADeletePreview(companyId);
  const deleteCompany = useDeleteCompany();
  const [confirmName, setConfirmName] = useState('');

  const canConfirm = preview?.canDelete && confirmName === companyName;

  const handleDelete = () => {
    if (!canConfirm) return;
    deleteCompany.mutate(companyId, { onSuccess: onDeleted });
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-[#222222] mb-1">Delete Organization</h3>
        <p className="text-sm text-[#3F3F3F] mb-4">
          This will permanently delete <strong>{companyName}</strong> and all associated data.
        </p>

        {isLoading ? (
          <p className="text-sm text-[#9A9A9A] mb-4">Loading...</p>
        ) : preview ? (
          <div className="mb-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 space-y-1 mb-4">
              <p className="font-medium">This action cannot be undone. It will delete:</p>
              <ul className="list-disc list-inside text-xs space-y-0.5">
                <li>{preview.userCount} user{preview.userCount !== 1 ? 's' : ''}</li>
                <li>{preview.leadCount} lead{preview.leadCount !== 1 ? 's' : ''}</li>
                <li>{preview.eventCount} event{preview.eventCount !== 1 ? 's' : ''}</li>
              </ul>
            </div>

            {preview.hasSuperAdmin ? (
              <p className="text-sm text-red-600 font-medium">
                Cannot delete: this organization contains a super admin user.
              </p>
            ) : (
              <div>
                <label className="text-sm font-medium text-[#3F3F3F] block mb-1">
                  Type <strong>{companyName}</strong> to confirm
                </label>
                <input
                  type="text"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={companyName}
                  className="w-full px-3 py-2 text-sm border border-[#DDDDDD] rounded-xl focus:outline-none focus:ring-2 focus:ring-red-300"
                  autoFocus
                />
              </div>
            )}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#3F3F3F] border border-[#DDDDDD] rounded-lg hover:bg-[#F7F7F7] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!canConfirm || deleteCompany.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {deleteCompany.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Users Tab ──────────────────────────────────────────────────────

function UsersTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({ field: 'createdAt', order: 'desc' });
  const { data, isLoading } = useSAUsers(page, search, undefined, sort);
  const toggleDisable = useToggleUserDisabled();
  const resetPassword = useTriggerPasswordReset();
  const [resetSent, setResetSent] = useState<string | null>(null);

  const handleSort = (field: string) => {
    setSort((prev) =>
      prev.field === field
        ? { field, order: prev.order === 'asc' ? 'desc' : 'asc' }
        : { field, order: 'asc' },
    );
    setPage(1);
  };

  const handleToggleDisable = (user: SAUser) => {
    if (user.isSuperAdmin) return;
    const action = user.isDisabled ? 'enable' : 'disable';
    if (!window.confirm(`${action === 'disable' ? 'Disable' : 'Enable'} ${user.fullName}?`)) return;
    toggleDisable.mutate({ userId: user.id, isDisabled: !user.isDisabled });
  };

  const handleResetPassword = (user: SAUser) => {
    if (!window.confirm(`Send password reset email to ${user.email}?`)) return;
    resetPassword.mutate(user.id, {
      onSuccess: () => {
        setResetSent(user.id);
        setTimeout(() => setResetSent(null), 3000);
      },
    });
  };

  const handleExport = () => {
    if (!data?.users) return;
    exportToCSV(
      ['Name', 'Email', 'Company', 'Role', 'Last Login', 'Status', 'Leads'],
      data.users.map((u) => [
        u.fullName,
        u.email,
        u.companyName,
        u.role,
        u.lastLoginAt ? formatDate(u.lastLoginAt) : '',
        u.isDisabled ? 'Disabled' : 'Active',
        u.leadCount,
      ]),
      'users-export',
    );
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search users..." />
        <ExportButton onClick={handleExport} disabled={!data?.users?.length} />
      </div>

      <div className="bg-white border border-[#DDDDDD] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#DDDDDD] bg-[#F7F7F7]">
              <SortableHeader label="Name" field="fullName" sort={sort} onSort={handleSort} />
              <SortableHeader label="Email" field="email" sort={sort} onSort={handleSort} />
              <SortableHeader label="Company" field="companyName" sort={sort} onSort={handleSort} />
              <SortableHeader label="Role" field="role" sort={sort} onSort={handleSort} align="center" />
              <SortableHeader label="Last Login" field="lastLoginAt" sort={sort} onSort={handleSort} />
              <SortableHeader label="Status" field="isDisabled" sort={sort} onSort={handleSort} align="center" />
              <SortableHeader label="Leads" field="leadCount" sort={sort} onSort={handleSort} align="center" />
              <th className="text-right px-4 py-3 font-medium text-[#9A9A9A] text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#9A9A9A]">Loading...</td></tr>
            ) : data?.users.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#9A9A9A]">No users found</td></tr>
            ) : (
              data?.users.map((u) => (
                <tr key={u.id} className="border-b border-[#DDDDDD] hover:bg-[#F7F7F7] transition-colors">
                  <td className="px-4 py-3 font-medium text-[#222222]">
                    <div className="flex items-center gap-1.5">
                      {u.fullName}
                      {u.isSuperAdmin && <ShieldCheck size={14} className="text-[#523EF7]" />}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#3F3F3F]">{u.email}</td>
                  <td className="px-4 py-3 text-[#3F3F3F]">{u.companyName}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={u.role === 'admin' ? 'info' : 'neutral'}>{u.role}</Badge>
                  </td>
                  <td className="px-4 py-3 text-[#3F3F3F]">{u.lastLoginAt ? formatDate(u.lastLoginAt) : '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={u.isDisabled ? 'danger' : 'success'}>
                      {u.isDisabled ? 'Disabled' : 'Active'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center text-[#3F3F3F]">{u.leadCount}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleToggleDisable(u)}
                        disabled={u.isSuperAdmin}
                        title={u.isDisabled ? 'Enable user' : 'Disable user'}
                        className={`p-1.5 rounded-lg transition-colors ${
                          u.isSuperAdmin
                            ? 'text-[#DDDDDD] cursor-not-allowed'
                            : u.isDisabled
                              ? 'text-emerald-600 hover:bg-emerald-50'
                              : 'text-[#9A9A9A] hover:bg-red-50 hover:text-red-600'
                        }`}
                      >
                        {u.isDisabled ? <CheckCircle2 size={16} /> : <Ban size={16} />}
                      </button>
                      <button
                        onClick={() => handleResetPassword(u)}
                        title="Send password reset"
                        className={`p-1.5 rounded-lg transition-colors ${
                          resetSent === u.id
                            ? 'text-emerald-600'
                            : 'text-[#9A9A9A] hover:bg-[#523EF7]/10 hover:text-[#523EF7]'
                        }`}
                      >
                        {resetSent === u.id ? <Check size={16} /> : <KeyRound size={16} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && data.totalPages > 1 && (
        <Pagination page={page} totalPages={data.totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}

// ─── Analytics Tab ──────────────────────────────────────────────────

function AnalyticsTab() {
  const { data: analytics } = useSAAnalytics();
  const { data: eventData } = useSAEventAnalytics();
  const setQuota = useSetEventQuota();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [sort, setSort] = useState<SortState>({ field: 'startDate', order: 'desc' });

  const handleStartEdit = (event: SAEvent) => {
    setEditingId(event.id);
    setEditValue(String(event.checksLimit));
  };

  const handleSaveQuota = (eventId: string) => {
    const num = parseInt(editValue);
    if (!isNaN(num) && num >= 0) {
      setQuota.mutate({ eventId, checksLimit: num });
    }
    setEditingId(null);
  };

  const handleSort = (field: string) => {
    setSort((prev) =>
      prev.field === field
        ? { field, order: prev.order === 'asc' ? 'desc' : 'asc' }
        : { field, order: 'asc' },
    );
  };

  const sortedEvents = eventData?.events ? [...eventData.events].sort((a, b) => {
    const dir = sort.order === 'asc' ? 1 : -1;
    const field = sort.field as keyof SAEvent;
    const aVal = a[field];
    const bVal = b[field];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'string' && typeof bVal === 'string') return aVal.localeCompare(bVal) * dir;
    if (typeof aVal === 'number' && typeof bVal === 'number') return (aVal - bVal) * dir;
    return String(aVal).localeCompare(String(bVal)) * dir;
  }) : [];

  const handleExport = () => {
    if (!sortedEvents.length) return;
    exportToCSV(
      ['Event', 'Company', 'Start Date', 'End Date', 'Leads', 'Checks Used', 'Checks Limit'],
      sortedEvents.map((e) => [
        e.name,
        e.companyName,
        formatDate(e.startDate),
        formatDate(e.endDate),
        e.leadCount,
        e.checksUsed,
        e.checksLimit,
      ]),
      'events-export',
    );
  };

  return (
    <div>
      {/* Summary cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <StatCard label="Companies" value={analytics.companyCount} icon={<Building2 size={18} />} />
          <StatCard label="Users" value={analytics.userCount} icon={<Users size={18} />} />
          <StatCard label="Leads" value={analytics.leadCount} />
          <StatCard label="Events" value={analytics.eventCount} />
          <StatCard label="Enrichments" value={analytics.enrichmentJobCount} />
          <StatCard label="Total Cost" value={`$${analytics.totalCostUsd.toFixed(2)}`} />
        </div>
      )}

      {/* Events table with quota editing */}
      <div className="flex items-center justify-end mb-4">
        <ExportButton onClick={handleExport} disabled={!sortedEvents.length} />
      </div>
      <div className="bg-white border border-[#DDDDDD] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#DDDDDD] bg-[#F7F7F7]">
              <SortableHeader label="Event" field="name" sort={sort} onSort={handleSort} />
              <SortableHeader label="Company" field="companyName" sort={sort} onSort={handleSort} />
              <SortableHeader label="Dates" field="startDate" sort={sort} onSort={handleSort} />
              <SortableHeader label="Leads" field="leadCount" sort={sort} onSort={handleSort} align="center" />
              <SortableHeader label="Usage" field="checksUsed" sort={sort} onSort={handleSort} align="center" />
              <SortableHeader label="Quota" field="checksLimit" sort={sort} onSort={handleSort} align="center" />
            </tr>
          </thead>
          <tbody>
            {sortedEvents.map((e) => (
              <tr key={e.id} className="border-b border-[#DDDDDD] hover:bg-[#F7F7F7] transition-colors">
                <td className="px-4 py-3 font-medium text-[#222222]">{e.name}</td>
                <td className="px-4 py-3 text-[#3F3F3F]">{e.companyName}</td>
                <td className="px-4 py-3 text-[#3F3F3F] text-xs">
                  {formatDate(e.startDate)} — {formatDate(e.endDate)}
                </td>
                <td className="px-4 py-3 text-center text-[#3F3F3F]">{e.leadCount}</td>
                <td className="px-4 py-3 text-center">
                  <UsageBar used={e.checksUsed} limit={e.checksLimit} />
                </td>
                <td className="px-4 py-3 text-center">
                  {editingId === e.id ? (
                    <div className="flex items-center justify-center gap-1">
                      <input
                        type="number"
                        value={editValue}
                        onChange={(ev) => setEditValue(ev.target.value)}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter') handleSaveQuota(e.id);
                          if (ev.key === 'Escape') setEditingId(null);
                        }}
                        className="w-16 px-2 py-1 text-center text-sm border border-[#DDDDDD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#523EF7]/30"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveQuota(e.id)}
                        className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="p-1 text-[#9A9A9A] hover:bg-[#F2F2F2] rounded"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleStartEdit(e)}
                      className="inline-flex items-center gap-1 text-[#3F3F3F] hover:text-[#523EF7] transition-colors"
                    >
                      {e.checksLimit}
                      <Pencil size={12} className="opacity-0 group-hover:opacity-100" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Sales Team Tab ─────────────────────────────────────────────────

function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

const SIGNUP_BASE_URL = 'https://insights.reevol.com/signup';

function SalesTeamTab() {
  const { data, isLoading } = useSASalesReps();
  const createRep = useCreateSalesRep();
  const updateRep = useUpdateSalesRep();
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [qrRepId, setQrRepId] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ field: 'createdAt', order: 'desc' });

  const handleSort = (field: string) => {
    setSort((prev) =>
      prev.field === field
        ? { field, order: prev.order === 'asc' ? 'desc' : 'asc' }
        : { field, order: 'asc' },
    );
  };

  const sortedReps = data?.salesReps ? [...data.salesReps].sort((a, b) => {
    const dir = sort.order === 'asc' ? 1 : -1;
    const field = sort.field as keyof SASalesRep;
    const aVal = a[field];
    const bVal = b[field];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'boolean' && typeof bVal === 'boolean') return (aVal === bVal ? 0 : aVal ? -1 : 1) * dir;
    if (typeof aVal === 'string' && typeof bVal === 'string') return aVal.localeCompare(bVal) * dir;
    if (typeof aVal === 'number' && typeof bVal === 'number') return (aVal - bVal) * dir;
    return String(aVal).localeCompare(String(bVal)) * dir;
  }) : [];

  const handleExport = () => {
    if (!sortedReps.length) return;
    exportToCSV(
      ['Name', 'Email', 'Slug', 'Referral Link', 'Referrals', 'Status'],
      sortedReps.map((r) => [
        r.name,
        r.email,
        r.slug,
        `${SIGNUP_BASE_URL}?ref=${r.slug}`,
        r.referralCount,
        r.isActive ? 'Active' : 'Inactive',
      ]),
      'sales-team-export',
    );
  };

  // Add form state
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addSlug, setAddSlug] = useState('');
  const [addError, setAddError] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editError, setEditError] = useState('');

  const handleAddNameChange = (name: string) => {
    setAddName(name);
    if (!slugManuallyEdited) {
      setAddSlug(nameToSlug(name));
    }
  };

  const handleAdd = () => {
    setAddError('');
    if (!addName.trim() || !addEmail.trim() || !addSlug.trim()) {
      setAddError('All fields are required');
      return;
    }
    createRep.mutate(
      { name: addName.trim(), email: addEmail.trim(), slug: addSlug.trim() },
      {
        onSuccess: () => {
          setShowAdd(false);
          setAddName('');
          setAddEmail('');
          setAddSlug('');
          setSlugManuallyEdited(false);
        },
        onError: (err) => setAddError(err instanceof Error ? err.message : 'Failed to create'),
      },
    );
  };

  const startEdit = (rep: SASalesRep) => {
    setEditingId(rep.id);
    setEditName(rep.name);
    setEditEmail(rep.email);
    setEditSlug(rep.slug);
    setEditError('');
  };

  const handleSaveEdit = () => {
    if (!editingId) return;
    setEditError('');
    updateRep.mutate(
      { id: editingId, name: editName.trim(), email: editEmail.trim(), slug: editSlug.trim() },
      {
        onSuccess: () => setEditingId(null),
        onError: (err) => setEditError(err instanceof Error ? err.message : 'Failed to update'),
      },
    );
  };

  const handleToggleActive = (rep: SASalesRep) => {
    const action = rep.isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`${action === 'deactivate' ? 'Deactivate' : 'Activate'} ${rep.name}?`)) return;
    updateRep.mutate({ id: rep.id, isActive: !rep.isActive });
  };

  const copyLink = (slug: string, repId: string) => {
    navigator.clipboard.writeText(`${SIGNUP_BASE_URL}?ref=${slug}`);
    setCopiedId(repId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const qrRep = data?.salesReps.find((r) => r.id === qrRepId);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-[#222222]">Sales Team</h2>
        <div className="flex items-center gap-2">
          <ExportButton onClick={handleExport} disabled={!sortedReps.length} />
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#523EF7] rounded-full hover:bg-[#4232D0] transition-colors shadow-sm"
          >
            <Plus size={16} />
            Add Rep
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#DDDDDD] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#DDDDDD] bg-[#F7F7F7]">
              <SortableHeader label="Name" field="name" sort={sort} onSort={handleSort} />
              <SortableHeader label="Email" field="email" sort={sort} onSort={handleSort} />
              <th className="text-left px-4 py-3 font-medium text-[#9A9A9A] text-xs uppercase tracking-wider">Referral Link</th>
              <SortableHeader label="Referrals" field="referralCount" sort={sort} onSort={handleSort} align="center" />
              <SortableHeader label="Status" field="isActive" sort={sort} onSort={handleSort} align="center" />
              <th className="text-right px-4 py-3 font-medium text-[#9A9A9A] text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Add form row */}
            {showAdd && (
              <tr className="border-b border-[#DDDDDD] bg-[#523EF7]/5">
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={addName}
                    onChange={(e) => handleAddNameChange(e.target.value)}
                    placeholder="Full name"
                    className="w-full px-2 py-1.5 text-sm border border-[#DDDDDD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#523EF7]/30"
                    autoFocus
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="email"
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                    placeholder="Email"
                    className="w-full px-2 py-1.5 text-sm border border-[#DDDDDD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#523EF7]/30"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={addSlug}
                    onChange={(e) => { setAddSlug(e.target.value); setSlugManuallyEdited(true); }}
                    placeholder="url-slug"
                    className="w-full px-2 py-1.5 text-sm border border-[#DDDDDD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#523EF7]/30"
                  />
                </td>
                <td />
                <td />
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {addError && <span className="text-xs text-red-500 mr-2">{addError}</span>}
                    <button onClick={handleAdd} disabled={createRep.isPending} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg">
                      <Check size={16} />
                    </button>
                    <button onClick={() => { setShowAdd(false); setAddError(''); }} className="p-1.5 text-[#9A9A9A] hover:bg-[#F2F2F2] rounded-lg">
                      <X size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#9A9A9A]">Loading...</td></tr>
            ) : sortedReps.length === 0 && !showAdd ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#9A9A9A]">No sales reps yet. Click "Add Rep" to get started.</td></tr>
            ) : (
              sortedReps.map((rep) =>
                editingId === rep.id ? (
                  <tr key={rep.id} className="border-b border-[#DDDDDD] bg-[#523EF7]/5">
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-[#DDDDDD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#523EF7]/30"
                        autoFocus
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-[#DDDDDD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#523EF7]/30"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={editSlug}
                        onChange={(e) => setEditSlug(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-[#DDDDDD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#523EF7]/30"
                      />
                    </td>
                    <td className="px-4 py-3 text-center text-[#3F3F3F]">{rep.referralCount}</td>
                    <td />
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {editError && <span className="text-xs text-red-500 mr-2">{editError}</span>}
                        <button onClick={handleSaveEdit} disabled={updateRep.isPending} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg">
                          <Check size={16} />
                        </button>
                        <button onClick={() => { setEditingId(null); setEditError(''); }} className="p-1.5 text-[#9A9A9A] hover:bg-[#F2F2F2] rounded-lg">
                          <X size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={rep.id} className="border-b border-[#DDDDDD] hover:bg-[#F7F7F7] transition-colors">
                    <td className="px-4 py-3 font-medium text-[#222222]">{rep.name}</td>
                    <td className="px-4 py-3 text-[#3F3F3F]">{rep.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[#9A9A9A] text-xs truncate max-w-[200px]">signup?ref={rep.slug}</span>
                        <button
                          onClick={() => copyLink(rep.slug, rep.id)}
                          title="Copy referral link"
                          className={`p-1 rounded transition-colors ${
                            copiedId === rep.id
                              ? 'text-emerald-600'
                              : 'text-[#9A9A9A] hover:text-[#523EF7]'
                          }`}
                        >
                          {copiedId === rep.id ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant="neutral">{rep.referralCount}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={rep.isActive ? 'success' : 'danger'}>
                        {rep.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => startEdit(rep)}
                          title="Edit"
                          className="p-1.5 text-[#9A9A9A] hover:bg-[#523EF7]/10 hover:text-[#523EF7] rounded-lg transition-colors"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(rep)}
                          title={rep.isActive ? 'Deactivate' : 'Activate'}
                          className={`p-1.5 rounded-lg transition-colors ${
                            rep.isActive
                              ? 'text-[#9A9A9A] hover:bg-red-50 hover:text-red-600'
                              : 'text-emerald-600 hover:bg-emerald-50'
                          }`}
                        >
                          {rep.isActive ? <Ban size={14} /> : <CheckCircle2 size={14} />}
                        </button>
                        <button
                          onClick={() => setQrRepId(rep.id)}
                          title="Generate QR Code"
                          className="p-1.5 text-[#9A9A9A] hover:bg-[#523EF7]/10 hover:text-[#523EF7] rounded-lg transition-colors"
                        >
                          <QrCode size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ),
              )
            )}
          </tbody>
        </table>
      </div>

      {/* QR Code Modal */}
      {qrRep && (
        <QRCodeModal
          slug={qrRep.slug}
          repName={qrRep.name}
          onClose={() => setQrRepId(null)}
        />
      )}
    </div>
  );
}

// ─── QR Code Modal ──────────────────────────────────────────────────

function QRCodeModal({ slug, repName, onClose }: { slug: string; repName: string; onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const signupUrl = `${SIGNUP_BASE_URL}?ref=${slug}`;

  const generateQR = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Dynamically import qrcode
    const QRCode = (await import('qrcode')).default;

    const size = 800;
    const padding = 60;
    const textAreaHeight = 100;
    const totalHeight = size + padding * 2 + textAreaHeight;
    const totalWidth = size + padding * 2;

    canvas.width = totalWidth;
    canvas.height = totalHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // White background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, totalWidth, totalHeight);

    // Generate QR as data URL
    const qrDataUrl = await QRCode.toDataURL(signupUrl, {
      errorCorrectionLevel: 'H',
      width: size,
      margin: 0,
      color: { dark: '#523EF7', light: '#FFFFFF' },
    });

    // Draw QR code
    const qrImage = new Image();
    await new Promise<void>((resolve) => {
      qrImage.onload = () => resolve();
      qrImage.src = qrDataUrl;
    });
    ctx.drawImage(qrImage, padding, padding, size, size);

    // Draw Reevol favicon icon in center
    try {
      const logoImage = new Image();
      logoImage.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        logoImage.onload = () => resolve();
        logoImage.onerror = () => reject();
        logoImage.src = '/favicon.png';
      });

      const logoSize = size * 0.18;
      const logoX = padding + (size - logoSize) / 2;
      const logoY = padding + (size - logoSize) / 2;

      // White rounded background behind logo for contrast
      const bgPad = 10;
      const bgX = logoX - bgPad;
      const bgY = logoY - bgPad;
      const bgW = logoSize + bgPad * 2;
      const bgH = logoSize + bgPad * 2;
      const radius = 12;
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.moveTo(bgX + radius, bgY);
      ctx.lineTo(bgX + bgW - radius, bgY);
      ctx.quadraticCurveTo(bgX + bgW, bgY, bgX + bgW, bgY + radius);
      ctx.lineTo(bgX + bgW, bgY + bgH - radius);
      ctx.quadraticCurveTo(bgX + bgW, bgY + bgH, bgX + bgW - radius, bgY + bgH);
      ctx.lineTo(bgX + radius, bgY + bgH);
      ctx.quadraticCurveTo(bgX, bgY + bgH, bgX, bgY + bgH - radius);
      ctx.lineTo(bgX, bgY + radius);
      ctx.quadraticCurveTo(bgX, bgY, bgX + radius, bgY);
      ctx.closePath();
      ctx.fill();

      ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
    } catch {
      // Logo load failed — QR still works without it
    }

    // Draw text below QR
    const textY = padding + size + 40;
    ctx.fillStyle = '#222222';
    ctx.font = 'bold 28px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Scan to try Reevol Insights', totalWidth / 2, textY);

    ctx.fillStyle = '#9A9A9A';
    ctx.font = '22px Inter, system-ui, sans-serif';
    ctx.fillText(repName, totalWidth / 2, textY + 36);

    setReady(true);
  }, [signupUrl, repName]);

  useEffect(() => {
    generateQR();
  }, [generateQR]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reevol-qr-${slug}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  const [linkCopied, setLinkCopied] = useState(false);
  const copyUrl = () => {
    navigator.clipboard.writeText(signupUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#222222]">QR Code — {repName}</h3>
          <button onClick={onClose} className="p-1 text-[#9A9A9A] hover:text-[#222222]">
            <X size={18} />
          </button>
        </div>

        {/* QR Preview */}
        <div className="flex justify-center mb-4">
          <canvas
            ref={canvasRef}
            className="w-[280px] h-auto rounded-xl border border-[#DDDDDD]"
          />
        </div>

        {/* Signup URL */}
        <div className="flex items-center gap-2 px-3 py-2 bg-[#F7F7F7] rounded-xl text-xs text-[#3F3F3F] mb-4">
          <Link2 size={14} className="text-[#9A9A9A] flex-shrink-0" />
          <span className="truncate">{signupUrl}</span>
          <button
            onClick={copyUrl}
            className={`ml-auto flex-shrink-0 p-1 rounded transition-colors ${
              linkCopied ? 'text-emerald-600' : 'text-[#9A9A9A] hover:text-[#523EF7]'
            }`}
          >
            {linkCopied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>

        {/* Download button */}
        <button
          onClick={handleDownload}
          disabled={!ready}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#523EF7] rounded-full hover:bg-[#4232D0] disabled:opacity-40 transition-colors"
        >
          <Download size={16} />
          Download PNG
        </button>
      </div>
    </div>
  );
}

// ─── Shared Components ──────────────────────────────────────────────

function SortableHeader({ label, field, sort, onSort, align }: {
  label: string;
  field: string;
  sort: SortState;
  onSort: (field: string) => void;
  align?: 'left' | 'center' | 'right';
}) {
  const isActive = sort.field === field;
  const textAlign = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  const justifyClass = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : '';
  return (
    <th
      onClick={() => onSort(field)}
      className={`${textAlign} px-4 py-3 font-medium text-xs uppercase tracking-wider cursor-pointer select-none transition-colors hover:bg-[#EEEEEE] ${
        isActive ? 'text-[#523EF7]' : 'text-[#9A9A9A]'
      }`}
    >
      <div className={`flex items-center gap-1 ${justifyClass}`}>
        {label}
        {isActive ? (
          sort.order === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
        ) : (
          <ArrowUpDown size={12} className="opacity-40" />
        )}
      </div>
    </th>
  );
}

function ExportButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title="Export CSV"
      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#3F3F3F] border border-[#DDDDDD] rounded-xl hover:bg-[#F7F7F7] hover:border-[#BBBBBB] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <Download size={14} />
      Export
    </button>
  );
}

function SearchBar({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative flex-1 max-w-sm">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-4 py-2.5 bg-white border border-[#DDDDDD] rounded-xl text-sm text-[#222222] placeholder:text-[#9A9A9A] focus:outline-none focus:ring-2 focus:ring-[#523EF7]/20 focus:border-[#523EF7]/40 transition-shadow"
      />
    </div>
  );
}

function Pagination({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (p: number) => void }) {
  return (
    <div className="flex items-center justify-center gap-2 mt-4">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="px-3 py-1.5 text-sm rounded-lg border border-[#DDDDDD] text-[#3F3F3F] hover:bg-[#F7F7F7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Previous
      </button>
      <span className="text-sm text-[#9A9A9A]">
        Page {page} of {totalPages}
      </span>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="px-3 py-1.5 text-sm rounded-lg border border-[#DDDDDD] text-[#3F3F3F] hover:bg-[#F7F7F7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Next
      </button>
    </div>
  );
}

function UsageBar({ used, limit }: { used: number; limit: number }) {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const color = pct >= 90 ? '#EF4444' : pct >= 70 ? '#F59E0B' : '#523EF7';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-[#E8E8E8] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs text-[#9A9A9A]">{used}/{limit}</span>
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
